/**
 * snapshotCrypto.ts — AES-256-GCM encryption for portable health snapshots.
 *
 * Two envelope versions exist. The decrypt path dispatches on
 * `envelope.version`:
 *
 *   v1 (legacy, no `version` field): SMK = PBKDF2-SHA256(
 *     ikm  = `${accountId}:${projectId}:snapshot-v1`,
 *     salt = serverSalt,
 *     c    = 100_000, dkLen = 32,
 *   ). Server-derivable — any party with `accountId`, `projectId`, and
 *   `salt_hex` from `project_snapshot_salts` can reconstruct the SMK. Kept
 *   forever so that snapshots issued before the v2 rollout remain readable.
 *
 *   v2 (`version: 2`): SMK = HKDF-SHA256(
 *     ikm  = accountRootKey (32 bytes, see `accountRootKey.ts`),
 *     salt = serverSalt,
 *     info = `eleplan-snapshot-v2:${accountId}:${projectId}`,
 *     len  = 32,
 *   ). The account root key is held in plaintext only on devices the user
 *   has authorised; the server only ever has it in wrapped form (mnemonic-
 *   stretched). Server state alone cannot derive the SMK. The server
 *   briefly sees the derived SMK during the one-shot encrypt request (sent
 *   as `encryption_key_hex`) and discards it.
 *
 * Cross-device portability comes from two channels that converge on the
 * same canonical account root key:
 *   - iCloud Keychain sync (iOS) of the unwrapped key — transparent.
 *   - Server-stored mnemonic wrap (`recoveryMnemonic.ts` + the
 *     `controllers/snapshots/recovery/*` endpoints) — universal fallback.
 * Either path delivers the same key bytes, so v2 envelopes open on any
 * device the user authorises.
 *
 * Salts in `project_snapshot_salts` MUST be treated as immutable. Rotating a
 * salt orphans every prior snapshot for that project (both v1 and v2) with
 * no recovery path. The backend `create-snapshot` controller enforces this
 * with `ON CONFLICT (project_id) DO NOTHING`, and a DB trigger (see
 * `migrations/protect-snapshot-salts-immutable.sql`) blocks any UPDATE.
 */

import * as SecureStore from 'expo-secure-store';
import * as Crypto from 'expo-crypto';
import { gcm } from '@noble/ciphers/aes.js';
import { pbkdf2Async } from '@noble/hashes/pbkdf2.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils.js';
import { getAccountRootKey } from './accountRootKey';

export { bytesToHex, hexToBytes };

export const SNAPSHOT_ENVELOPE_VERSION_V1 = 1 as const;
export const SNAPSHOT_ENVELOPE_VERSION_V2 = 2 as const;
export type SnapshotEnvelopeVersion =
  | typeof SNAPSHOT_ENVELOPE_VERSION_V1
  | typeof SNAPSHOT_ENVELOPE_VERSION_V2;

// Same accessibility policy as vaultCrypto — never backed up to iCloud Keychain.
const KEYCHAIN_ACCESSIBLE = SecureStore.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY;

/**
 * Cache keys are partitioned by envelope version so that a v1 SMK cached
 * while opening a legacy snapshot cannot be mistakenly served as a v2 key
 * for a later envelope (or vice versa). The two SMKs are incompatible and
 * would produce a silent decrypt failure under a shared cache slot.
 */
const smkKey = (
  projectId: number | string,
  version: SnapshotEnvelopeVersion,
) => `snap_smk_v${version}_${projectId}`;

export interface SnapshotEncryptedBlob {
  encrypted_data: string;  // hex-encoded AES-256-GCM ciphertext
  iv: string;              // hex-encoded 12-byte nonce
  auth_tag: string;        // hex-encoded 16-byte GCM authentication tag
  // Optional metadata fields emitted by the server. Absent on the oldest v1
  // envelopes; present on every envelope written on or after v2 rollout.
  version?: SnapshotEnvelopeVersion;
  schema_version?: number;
  snapshot_version?: number;
  created_at?: string;
  checksum?: string;
  size_bytes?: number;
}

/** v1: PBKDF2, server-derivable. Kept for back-compat with legacy envelopes. */
export async function deriveSnapshotKey(
  accountId: string,
  projectId: number | string,
  saltHex: string,
): Promise<Uint8Array> {
  const input = utf8ToBytes(`${accountId}:${projectId}:snapshot-v1`);
  const salt = hexToBytes(saltHex);
  return pbkdf2Async(sha256, input, salt, { c: 100_000, dkLen: 32 });
}

/**
 * v2: HKDF-SHA256 with the canonical account root key as the IKM. The root
 * key lives in `accountRootKey.ts`; the server only holds it in wrapped
 * form, so server state alone cannot derive this SMK.
 */
export function deriveSnapshotKeyV2(
  accountRootKey: Uint8Array,
  accountId: string,
  projectId: number | string,
  saltHex: string,
): Uint8Array {
  const info = utf8ToBytes(`eleplan-snapshot-v2:${accountId}:${projectId}`);
  const salt = hexToBytes(saltHex);
  return hkdf(sha256, accountRootKey, salt, info, 32);
}

/**
 * Envelope version dispatch. Pre-v2 envelopes don't carry `version`, so we
 * treat an absent/unknown value as v1. Any explicit non-1 value is honored
 * (currently only 2). Returning the literal lets callers `switch` on it
 * exhaustively.
 */
export function getEnvelopeVersion(
  blob: Pick<SnapshotEncryptedBlob, 'version'>,
): SnapshotEnvelopeVersion {
  return blob?.version === SNAPSHOT_ENVELOPE_VERSION_V2
    ? SNAPSHOT_ENVELOPE_VERSION_V2
    : SNAPSHOT_ENVELOPE_VERSION_V1;
}

/**
 * Derive the SMK for an arbitrary envelope by picking the right KDF for its
 * version. For v2, the canonical account root key is fetched via
 * `getAccountRootKey` (local SecureStore, with iCloud-Keychain promotion
 * on first read on a new iOS device). Returns null if v2 is requested but
 * the root key is not installed AND the synced layer hasn't delivered it
 * — caller is responsible for routing the user through the mnemonic
 * recovery flow before retrying.
 */
export async function deriveSmkForEnvelope(
  blob: Pick<SnapshotEncryptedBlob, 'version'>,
  accountId: string,
  projectId: number | string,
  saltHex: string,
): Promise<Uint8Array | null> {
  const version = getEnvelopeVersion(blob);
  if (version === SNAPSHOT_ENVELOPE_VERSION_V2) {
    const rootKey = await getAccountRootKey(accountId);
    if (!rootKey) return null;
    return deriveSnapshotKeyV2(rootKey, accountId, projectId, saltHex);
  }
  return deriveSnapshotKey(accountId, projectId, saltHex);
}

/**
 * Derive the SMK for a NEW snapshot. Returns null if the account root key
 * is not installed on this device — the caller MUST route to the recovery
 * setup / recovery prompt before retrying. We deliberately do NOT lazily
 * generate a key here, because that would create a fresh per-device key
 * that can't open snapshots from other devices (the bug the recovery
 * model exists to fix).
 */
export async function deriveSmkForNewSnapshot(
  accountId: string,
  projectId: number | string,
  saltHex: string,
): Promise<Uint8Array | null> {
  const rootKey = await getAccountRootKey(accountId);
  if (!rootKey) return null;
  return deriveSnapshotKeyV2(rootKey, accountId, projectId, saltHex);
}

async function writeSmkSecure(key: string, hexSmk: string): Promise<void> {
  const opts = {
    requireAuthentication: true,
    authenticationPrompt: 'Confirm identity to access health backup',
    keychainAccessible: KEYCHAIN_ACCESSIBLE,
  };
  try {
    await SecureStore.setItemAsync(key, hexSmk, opts);
  } catch {
    // Android Keystore does not allow in-place updates of biometric-protected keys.
    await SecureStore.deleteItemAsync(key).catch(() => {});
    await SecureStore.setItemAsync(key, hexSmk, opts);
  }
}

/** Cache the derived SMK in the device's secure enclave / Android Keystore. */
export async function cacheSnapshotKey(
  projectId: number | string,
  smk: Uint8Array,
  options: { version: SnapshotEnvelopeVersion } = { version: SNAPSHOT_ENVELOPE_VERSION_V2 },
): Promise<void> {
  await writeSmkSecure(smkKey(projectId, options.version), bytesToHex(smk));
}

/**
 * Load the cached SMK from SecureStore. Triggers a biometric prompt.
 * Returns null if not found or the user cancels authentication.
 */
export async function loadSnapshotKey(
  projectId: number | string,
  options: {
    version?: SnapshotEnvelopeVersion;
    prompt?: string;
  } = {},
): Promise<Uint8Array | null> {
  const { version = SNAPSHOT_ENVELOPE_VERSION_V2, prompt = 'Unlock health backup' } = options;
  try {
    const hex = await SecureStore.getItemAsync(smkKey(projectId, version), {
      requireAuthentication: true,
      authenticationPrompt: prompt,
    });
    return hex ? hexToBytes(hex) : null;
  } catch {
    return null;
  }
}

/** Remove the cached SMK for all known versions (e.g. on sign-out or reset). */
export async function clearSnapshotKey(projectId: number | string): Promise<void> {
  await Promise.all([
    SecureStore.deleteItemAsync(smkKey(projectId, SNAPSHOT_ENVELOPE_VERSION_V1)).catch(() => {}),
    SecureStore.deleteItemAsync(smkKey(projectId, SNAPSHOT_ENVELOPE_VERSION_V2)).catch(() => {}),
    // Legacy pre-versioned cache key from before v2 rollout.
    SecureStore.deleteItemAsync(`snap_smk_${projectId}`).catch(() => {}),
  ]);
}

/** Generate a random 32-byte one-time key for a single snapshot share. */
export async function generateSnapshotShareKey(): Promise<Uint8Array> {
  const buf = await Crypto.getRandomBytesAsync(32);
  return new Uint8Array(buf);
}

/**
 * Encrypt a snapshot JSON payload with AES-256-GCM. `version` is stamped on
 * the envelope so the decrypt path can dispatch to the right KDF later. Share
 * envelopes omit the version (the recipient decrypts with a one-shot key
 * from the URL fragment and doesn't touch the SMK path at all).
 */
export async function encryptSnapshot(
  smk: Uint8Array,
  snapshotJson: object,
  options: { version?: SnapshotEnvelopeVersion } = {},
): Promise<SnapshotEncryptedBlob> {
  const ivBuffer = await Crypto.getRandomBytesAsync(12);
  const iv = new Uint8Array(ivBuffer);
  const plaintext = utf8ToBytes(JSON.stringify(snapshotJson));
  const cipher = gcm(smk, iv);
  const ciphertext = cipher.encrypt(plaintext);
  const data = ciphertext.slice(0, -16);
  const tag = ciphertext.slice(-16);
  const blob: SnapshotEncryptedBlob = {
    encrypted_data: bytesToHex(data),
    iv: bytesToHex(iv),
    auth_tag: bytesToHex(tag),
  };
  if (options.version) blob.version = options.version;
  return blob;
}

/**
 * Decrypt a snapshot blob. Returns the parsed snapshot object, or null if the
 * key or ciphertext is invalid (wrong key, corrupted file, tampered data).
 */
export function decryptSnapshot(
  smk: Uint8Array,
  blob: SnapshotEncryptedBlob,
): object | null {
  try {
    const ivBytes = hexToBytes(blob.iv);
    const dataBytes = hexToBytes(blob.encrypted_data);
    const tagBytes = hexToBytes(blob.auth_tag);
    const ciphertext = new Uint8Array(dataBytes.length + tagBytes.length);
    ciphertext.set(dataBytes);
    ciphertext.set(tagBytes, dataBytes.length);
    const cipher = gcm(smk, ivBytes);
    const plaintext = cipher.decrypt(ciphertext);
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    return null;
  }
}

/**
 * Re-encrypt an existing encrypted snapshot for sharing using a one-time share
 * key. The share-encrypted blob is what gets uploaded to S3 — the original SMK
 * is never exposed.
 *
 * The shareKey is embedded in the URL fragment on the client and never sent to
 * the server.
 */
export async function encryptSnapshotForShare(
  smk: Uint8Array,
  existingBlob: SnapshotEncryptedBlob,
  shareKey: Uint8Array,
): Promise<SnapshotEncryptedBlob> {
  // Decrypt with SMK, re-encrypt with share key.
  const plainObject = decryptSnapshot(smk, existingBlob);
  if (!plainObject) throw new Error('Failed to decrypt snapshot for share — invalid key or corrupted blob.');
  return encryptSnapshot(shareKey, plainObject);
}

/**
 * Decrypt a share-encrypted blob using the share key extracted from the URL
 * fragment. Returns the parsed snapshot object, or null on failure.
 */
export function decryptSnapshotFromShare(
  shareKey: Uint8Array,
  blob: SnapshotEncryptedBlob,
): object | null {
  return decryptSnapshot(shareKey, blob);
}
